import React from 'react';
import styled from '@emotion/styled';
import { palette, radius, spacing, status, typography } from '../../../../style-utils/theme';
import { OWNER_HATCH, PROVIDER_HATCH } from './calendar-day';

/**
 * WHAT THE CALENDAR'S MARKS MEAN — every swatch has its word beside it, so no
 * mark is colour alone.
 */

type SwatchKind = 'today' | 'stay' | 'arrival' | 'departure' | 'block' | 'provider' | 'gap' | 'gap1' | 'turnaround';

const LEGEND: ReadonlyArray<{ kind: SwatchKind; label: string }> = [
  { kind: 'today', label: 'Today' },
  { kind: 'stay', label: 'Stay' },
  { kind: 'arrival', label: 'Check-in' },
  { kind: 'departure', label: 'Check-out' },
  { kind: 'block', label: 'Owner block' },
  { kind: 'provider', label: 'Channel block' },
  { kind: 'gap', label: 'Gap night' },
  { kind: 'gap1', label: 'One-night gap' },
  { kind: 'turnaround', label: 'Turnaround' },
];

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs} ${spacing.sm};
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;
`;

const Item = styled.li`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.inkSoft};
`;

const swatchBg = ({ $kind }: { $kind: SwatchKind }): string => {
  switch ($kind) {
    case 'today':
      return palette.surface;
    case 'stay':
    case 'arrival':
      return status.brand.bg;
    case 'departure':
      return status.neutral.bg;
    case 'block':
      return OWNER_HATCH;
    case 'provider':
      return PROVIDER_HATCH;
    case 'gap':
      return palette.slate;
    case 'gap1':
      return palette.warning;
    default:
      return status.warning.bg;
  }
};

const Swatch = styled.span<{ $kind: SwatchKind }>`
  position: relative;
  flex: none;
  /* Dots are 8 x 8, today's ring 16 x 16, every bar 16 x 8 (a data mark: its
     short side is under 12px, so it takes the 3px mark radius). */
  width: ${({ $kind }) => ($kind === 'gap' || $kind === 'gap1' ? spacing.xs : spacing.sm)};
  height: ${({ $kind }) => ($kind === 'today' ? spacing.sm : spacing.xs)};
  border-radius: ${({ $kind }) => ($kind === 'today' || $kind === 'gap' || $kind === 'gap1' ? radius.circle : '3px')};
  background: ${swatchBg};
  box-shadow: ${({ $kind }) => ($kind === 'today' ? `0 0 0 2px ${palette.lagoon}` : 'none')};
  border: ${({ $kind }) => ($kind === 'departure' || $kind === 'turnaround' ? `1px solid ${palette.lineStrong}` : 'none')};

  &::before {
    content: '';
    display: ${({ $kind }) => ($kind === 'arrival' ? 'block' : 'none')};
    position: absolute;
    top: 2px;
    bottom: 2px;
    left: 3px;
    width: 2px;
    border-radius: 2px;
    background: ${palette.teal};
  }
`;

export const CalendarLegend: React.FC = () => (
  <List aria-label="Legend" data-calendar-legend="">
    {LEGEND.map(item => (
      <Item key={item.kind} data-legend={item.kind}>
        <Swatch $kind={item.kind} aria-hidden="true" />
        {item.label}
      </Item>
    ))}
  </List>
);
